import { HcResponseMeaning } from './hc-response-meaning.enum';
import { HcMessageStrings } from './strings/hc-message-strings';

/**
 * Determines the meaning of a free-form response based on a given language code.
 * Returns a value from HcResponseMeaning when language-specific keywords match,
 * otherwise returns null to allow backend AI resolution.
 */
export function determineHcResponseMeaning(language: string, text: string): HcResponseMeaning | null {
  if (!text) {
    return null;
  }

  const normalizedText = normalizeText(text);

  try {
    // Get the meaningsKeyword from HcMessageStrings
    const callMessages = HcMessageStrings.getCallMessages(language);
    const meaningsKeyword = callMessages.meaningsKeyword;

    // French: carrier / handset auto-replies often contain "bien reçu" — not a health OK answer.
    if (language === 'fr' && frenchLooksLikeCarrierReceiptAck(normalizedText)) {
      return null;
    }

    // Check NOT_OK keywords FIRST (more specific, e.g., "not ok" should match before "ok")
    if (matchesAny(normalizedText, meaningsKeyword.notOk)) {
      return HcResponseMeaning.NOT_OK;
    }

    // French: standalone reply "bien" / "bien." (prompt asks for it); phrase OKs use keywords list below.
    if (language === 'fr') {
      const bare = normalizedText.replace(/[.!?…]+$/gu, '').trim();
      if (/^bien$/u.test(bare) && !frenchLooksLikeMerciBienThanks(normalizedText)) {
        return HcResponseMeaning.OK;
      }
    }

    // Check OK keywords (after NOT_OK to avoid false matches)
    if (matchesAny(normalizedText, meaningsKeyword.ok)) {
      return HcResponseMeaning.OK;
    }

    // Check NEED_HELP keywords
    if (matchesAny(normalizedText, meaningsKeyword.needHelp)) {
      return HcResponseMeaning.NEED_HELP;
    }
  } catch (error) {
    // If language is not supported or there's an error, return null
    // This allows backend AI to handle the response
    return null;
  }

  return null;
}

/** Carriers often send "… bien reçu …" — not the prompted health reply "bien". */
function frenchLooksLikeCarrierReceiptAck(normalizedText: string): boolean {
  return /\bbien\s+recu\b/.test(normalizedText);
}

/** "Merci bien" is polite thanks in FR — not the single-token OK reply "bien". */
function frenchLooksLikeMerciBienThanks(normalizedText: string): boolean {
  return /\bmerci\s+bien\b/.test(normalizedText);
}

function normalizeText(value: string): string {
  // Lowercase and strip diacritics to improve simple matching without heavy logic
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}+/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function matchesAny(text: string, candidates: string[]): boolean {
  // Sort candidates by length (longest first) to match more specific phrases first
  // This ensures "not ok" matches before "ok" when both are in the same category
  const sortedCandidates = [...candidates].sort((a, b) => {
    const normA = normalizeText(a);
    const normB = normalizeText(b);
    return normB.length - normA.length; // Descending order (longest first)
  });

  for (const cand of sortedCandidates) {
    const norm = normalizeText(cand);

    // 1. Exact match (highest priority)
    if (text === norm) {
      return true;
    }

    // 2. For multi-word phrases, check if the entire phrase appears in the text
    if (norm.includes(' ')) {
      if (text.includes(norm)) {
        return true;
      }
    } else {
      // 3. For single words, use word boundary matching to avoid false positives
      // This prevents "ok" from matching in "not ok"
      const wordBoundaryRegex = new RegExp(`\\b${escapeRegex(norm)}\\b`, 'i');
      if (wordBoundaryRegex.test(text)) {
        return true;
      }
      // No substring fallback for short keywords: it falsely matched "ok" inside unrelated words
      // (e.g. "book", "token"), completing SMS health-check listen windows without a real reply.
    }
  }
  return false;
}

function escapeRegex(str: string): string {
  // Escape special regex characters
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
